// ============================================
// SolCheddar Chart Utilities
// Pure functions & constants — no React, no side effects
// ============================================

import { candleTotalVolume } from './candleHelpers'

// --- Constants ---
export const IMBALANCE_RATIO = 5
export const MAX_CANDLES = 500
export const MAX_TRADES_WINDOW_MS = 60 * 60 * 1000
// WS_URL resolution order:
//  1. VITE_WS_URL env var if explicitly set (escape hatch)
//  2. Same hostname the page was loaded from, port 8765 — works for both
//     local dev (localhost:8765) AND Tailscale users hitting the dev
//     machine via 100.x.x.x without any per-user .env config.
// SSR safety: window can be undefined during build; fall back to localhost.
export const WS_URL = import.meta.env.VITE_WS_URL ||
  (typeof window !== 'undefined'
    ? `ws://${window.location.hostname}:8765`
    : 'ws://localhost:8765')

// HTTP_PROXY_BASE — backend's small HTTP proxy on port 8766. Used to bypass
// CORS on third-party APIs the browser can't hit directly (e.g.
// GeckoTerminal). Same hostname-derivation as WS_URL so Tailscale users
// hit the same machine on a different port without per-user env config.
export const HTTP_PROXY_BASE = import.meta.env.VITE_HTTP_PROXY_BASE ||
  (typeof window !== 'undefined'
    ? `http://${window.location.hostname}:8766`
    : 'http://localhost:8766')
export const WS_MAX_RECONNECT_ATTEMPTS = 10
export const WS_HEARTBEAT_INTERVAL = 30000
export const TOAST_DURATION = 3000
export const TOAST_ERROR_DURATION = 5000
export const ROW_HEIGHT = 24
export const MIN_VIEW_WINDOW = 5
export const MAX_VIEW_WINDOW = 200
export const DEFAULT_VIEW_WINDOW = 20
export const TARGET_FOOTPRINT_CANDLE_PX = 36
// Phone / narrow plot: wider columns so initial fit matches desktop candle feel
// (narrow width + 36px target packed too many skinny candles).
export const TARGET_FOOTPRINT_CANDLE_PX_NARROW = 52
// Plot CSS px below this → NARROW target. Room question, not device: matches
// useIsPhone's "available ROOM" guidance. ~768 catches phone portrait +
// narrow desktop panes; wide desktop plots keep 36.
export const FOOTPRINT_NARROW_PLOT_W = 768
// Candle count so each column is ~targetPx wide on this plot.
// Wider chart → more candles; narrower → fewer (and on narrow plots, each
// candle is sized to TARGET_FOOTPRINT_CANDLE_PX_NARROW). Home / token / auto-fit.
export function footprintWinForPlotW(plotW, targetPx) {
  const pw = Math.max(0, +plotW || 0)
  const px = (targetPx != null && +targetPx > 0)
    ? +targetPx
    : (pw > 0 && pw < FOOTPRINT_NARROW_PLOT_W
        ? TARGET_FOOTPRINT_CANDLE_PX_NARROW
        : TARGET_FOOTPRINT_CANDLE_PX)
  if (!(pw > 0) || !(px > 0)) return DEFAULT_VIEW_WINDOW
  return Math.max(MIN_VIEW_WINDOW, Math.min(MAX_VIEW_WINDOW, Math.round(pw / px)))
}
export const RIGHT_PADDING = 0

// Padding when autoscroll is ON — number of empty candle slots between
// the latest real candle and the right edge of the chart. Smaller = the
// live candle hugs the edge more tightly.
export const AUTO_PADDING = 1

// Number of empty "future" candle slots the user can drag-scroll into
// past the latest real candle. Honored by every site that clamps or
// resolves viewEndIndex, otherwise drag anchors go stale. Raised from 3
// to give room to pull the footprint LEFT (opening right-side gutter for
// the smoketrail / volume profile). Autoscroll still rests at AUTO_PADDING
// (1), so this only widens how far a manual drag can push the chart forward.
export const FUTURE_PADDING = 40

// Fixed-width overlay on the right side of the canvas (drawn directly on
// canvas — separator line, axis backdrop, volume profile bars). The DOM
// .y-axis-body's min-width should match this — they share visual space.
export const FIXED_OVERLAY_WIDTH = 60

export const MCAP_BUCKET_STEPS = (() => {
  // Densified ~2× (max adjacent ratio 1.2, was 1.33). Superset of the
  // old set [1,1.25,1.5,2,2.5,3,4,5,6,7.5] so nothing that worked
  // regresses — this only adds rungs between existing ones. Fixes the
  // "dead wheel travel" (small-dataRange tokens like ETH had only ~3
  // bucket steps across the whole zoom range → most notches did nothing;
  // now ~2× the rungs in that band). Scale-independent: geometric, so
  // every decade gets the same finer spacing.
  const mantissas = [1, 1.1, 1.25, 1.4, 1.5, 1.75, 2, 2.25, 2.5, 3, 3.5, 4, 4.5, 5, 6, 7, 7.5, 8, 9]
  const steps = []
  for (let exp = -8; exp <= 12; exp++) {
    const base = Math.pow(10, exp)
    for (const m of mantissas) {
      // m * base reintroduces float error (6 * 0.1 -> 0.6000000000000001).
      // Number(x.toPrecision(12)) formats to 12 sig-figs and reparses —
      // reliably yields the clean value (0.6) while staying far more
      // precise than any step gap, so selection is unchanged. Purely a
      // representation cleanup (logs, labels, equality checks).
      steps.push(Number((m * base).toPrecision(12)))
    }
  }
  return steps
})()

// --- Format helpers ---
export function formatMcap(mcap) {
  const abs = Math.abs(mcap)
  if (abs >= 1e9) return (mcap / 1e9).toFixed(2) + 'B'
  if (abs >= 1e6) return (mcap / 1e6).toFixed(2) + 'M'
  if (abs >= 1e3) return (mcap / 1e3).toFixed(2) + 'K'
  return mcap.toFixed(0)
}

export function formatPrice(price, rowStep = null) {
  // Decimals scale to magnitude so futures prints don't visually collapse
  // onto the same label. The old $1–$100 tier used toFixed(2), so an XRP
  // print at 1.1438 rendered "1.14" and appeared to float between axis
  // rows even though it sits at its true sub-cent bucket. HL streams full
  // float precision; only display was coarse. Tiers now give 4 decimals
  // through the single digits (XRP, DOGE, SOL sub-$10), 3 in the tens,
  // tapering as magnitude makes finer digits meaningless.
  //
  // rowStep (optional): absolute price width of the lattice row being
  // labeled. When the axis is finer than the magnitude tier (e.g. $101.90
  // with ~0.005–0.01 rows), toFixed(2) prints the SAME string on two
  // rows and the 101.89 line looks "missing". Bump decimals until a
  // one-row move can change the label.
  const abs = Math.abs(price)
  if (abs >= 10000) return price.toLocaleString(undefined, { maximumFractionDigits: 0 })
  let decimals
  if (abs >= 1000) decimals = 1
  else if (abs >= 100) decimals = 2
  else if (abs >= 10) decimals = 3
  else if (abs >= 1) decimals = 4
  else if (abs >= 0.01) decimals = 5
  else return price.toPrecision(4)
  if (rowStep > 0 && Number.isFinite(rowStep)) {
    const need = Math.min(8, Math.max(0, Math.ceil(-Math.log10(rowStep) - 1e-12)))
    decimals = Math.max(decimals, need)
  }
  return price.toFixed(decimals)
}

export function formatNumber(n) {
  const abs = Math.abs(n)
  if (abs >= 1e9) return (n / 1e9).toFixed(2) + 'B'
  if (abs >= 1e6) return (n / 1e6).toFixed(2) + 'M'
  if (abs >= 1e3) return (n / 1e3).toFixed(2) + 'K'
  return n.toFixed(0)
}

export function formatCompact(n) {
  const abs = Math.abs(n)
  if (abs >= 1e9) return Math.round(n / 1e9) + 'B'
  if (abs >= 1e6) return Math.round(n / 1e6) + 'M'
  if (abs >= 1e3) return Math.round(n / 1e3) + 'K'
  if (abs >= 1) return Math.round(n)
  if (abs >= 0.01) return n.toFixed(2)
  if (abs >= 0.001) return n.toFixed(3)
  if (abs > 0) return n.toFixed(4)
  return 0
}

// Cell-volume formatter for footprint cells.
// Drops K/M/B suffix and rounds to integer in each magnitude band — the cell's
// color intensity and column context already communicate scale, so the bare
// number wins on horizontal cell space and visual quiet. "1.5M" → "1",
// "250K" → "250", "1234" → "1", "50" → "50".
// cmeexact-20261102: CME contracts print whole, never abbreviated.
export function formatCellVolExact(n) {
  const v = Math.round(+n || 0)
  return v ? String(v) : '0'
}

export function formatCellVol(n) {
  const abs = Math.abs(n)
  if (abs >= 1e9) return String(Math.round(n / 1e9))
  if (abs >= 1e6) return String(Math.round(n / 1e6))
  if (abs >= 1e3) return String(Math.round(n / 1e3))
  if (abs >= 1)   return String(Math.round(n))
  if (abs >= 0.1) return n.toFixed(1)
  if (abs > 0)    return n.toFixed(2)
  return '0'
}

// Smallest p such that bucketSize * 10^p is an integer (within FP tolerance).
// The naive `Math.ceil(-Math.log10(bucketSize))` gives precision=1 for 0.25,
// which makes `Math.round(0.25 * 10)` = 3 — silently treating the bucket as
// 0.3. This helper walks p upward until the product is close to an integer,
// which is correct for every value in MCAP_BUCKET_STEPS (0.25, 0.125, 0.75,
// 0.15, etc.).
function bucketPrecision(bucketSize) {
  if (bucketSize >= 1) return 0
  for (let p = 1; p < 15; p++) {
    const scaled = bucketSize * Math.pow(10, p)
    if (Math.abs(scaled - Math.round(scaled)) < 1e-9) return p
  }
  return 8
}

// --- Bucket snap (avoids FP drift with fractional bucket sizes) ---
export function snapBucket(value, bucketSize) {
  if (bucketSize >= 1) return Math.floor(value / bucketSize) * bucketSize
  // Scale to integer space, do integer math, scale back.
  const precision = bucketPrecision(bucketSize)
  const factor = Math.pow(10, precision)
  const scaledBucket = Math.round(bucketSize * factor)
  const scaledValue = Math.floor(value * factor + 1e-9)
  const step = Math.floor(scaledValue / scaledBucket)
  return (step * scaledBucket) / factor
}

// Ceil to next bucket boundary using the same integer-step math as snapBucket.
// Use this anywhere you'd reach for Math.ceil(value / bucketSize) * bucketSize
// on a fractional bucketSize — naïve FP drifts by one step at some zoom levels.
export function ceilBucket(value, bucketSize) {
  if (bucketSize >= 1) return Math.ceil(value / bucketSize) * bucketSize
  const precision = bucketPrecision(bucketSize)
  const factor = Math.pow(10, precision)
  const scaledBucket = Math.round(bucketSize * factor)
  const scaledValue = Math.ceil(value * factor - 1e-9)
  const step = Math.ceil(scaledValue / scaledBucket)
  return (step * scaledBucket) / factor
}

// Find the index of a value in sortedMcaps using integer step math
// instead of indexOf (which fails with floating point)
export function findBucketIndex(sortedMcaps, value, bucketSize) {
  if (sortedMcaps.length < 2 || value <= 0) return -1
  // LATTICE-RELATIVE membership — MUST stay identical to bucketRowIndex()
  // in FootprintChartCanvas (the chip/band mapping). These previously
  // disagreed: this fn had an `indexOf(value)` exact-float-match branch
  // for bucketSize>=1 (any non-bit-identical price returned -1, so volume
  // bars/markers silently vanished) and a Math.round (nearest) fractional
  // branch (picked a different row than the chip's floor/ceil membership
  // mid-bucket). Result: volume-profile bars and price markers drifted a
  // row from the highlight chip, worst at the lows where the autoscroll
  // stretch unaligns the lattice from round mcaps. One geometry now: row i
  // covers [mcaps[i], mcaps[i] + B), i = ceil((mcaps[0] - value) / B),
  // epsilon-guarded. The bucketSize arg is kept for call-site
  // compatibility but the spacing is derived from the lattice itself, so
  // it's correct even when the committed bucket lags the rendered grid.
  const idx = latticeRowIndex(sortedMcaps, value)
  if (idx >= 0 && idx < sortedMcaps.length) return idx
  return -1
}

// ── The one price→row mapping ──────────────────────────────────────
// findBucketIndex above, bucketRowIndex in chartGeometry, and the chip/band
// mapping in FootprintChartCanvas were three copies of one expression:
//     ceil((top − value) / (top − next))
// The note above records what it cost when they last disagreed — volume bars
// and price markers drifting a row from the highlight chip. One copy now.
//
// LATTICE-AGNOSTIC. That expression is "how far is `value` from the top, in
// units of one row". On a LINEAR lattice a row is a constant DIFFERENCE; on a
// LOG lattice it is a constant RATIO, and the same expression under log gives
// the same answer:
//     linear: (top − value) / (top − next)
//     log:    ln(top / value) / ln(top / next)
// Nothing downstream cares which: rows are uniform in PIXELS either way
// (y = i * RH), and every other consumer only needs the lattice to be
// monotone, which both are.
//
// The mode travels ON THE ARRAY (`mcaps.log`), the same way visibleCount and
// rowH already do, so no call site has to carry it and an untagged array —
// every array today — takes the linear branch and behaves exactly as before.
export function latticeRowIndex(mcaps, value) {
  if (!mcaps || mcaps.length < 2 || !(value > 0)) return -1
  const top = mcaps[0]
  if (mcaps.log) {
    const step = Math.log(top / mcaps[1])
    if (!(step > 0)) return -1
    return Math.ceil(Math.log(top / value) / step - 1e-9)
  }
  const bucket = top - mcaps[1]
  if (!(bucket > 0)) return -1
  return Math.ceil((top - value) / bucket - 1e-9)
}

// Nearest row in the LIVE lattice array (descending). For footprint display
// levels that are supposed to sit on axis rows: geometric round/ceil against
// a slightly out-of-phase spacing collapses two levels onto one index and
// skips the next — visible as a gap then a crushed pair. Searching the
// actual mcaps[] ties every cell to an axis label by construction.
export function nearestLatticeRow(mcaps, value) {
  if (!mcaps || !mcaps.length || !(value > 0)) return -1
  const n = mcaps.length
  if (n === 1) return 0
  // Descending: mcaps[0] high → mcaps[n-1] low.
  if (value >= mcaps[0]) return 0
  if (value <= mcaps[n - 1]) return n - 1
  let lo = 0, hi = n - 1
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (mcaps[mid] >= value) lo = mid
    else hi = mid
  }
  if (mcaps.log) {
    const dLo = Math.abs(Math.log(mcaps[lo] / value))
    const dHi = Math.abs(Math.log(mcaps[hi] / value))
    return dLo <= dHi ? lo : hi
  }
  return (Math.abs(mcaps[lo] - value) <= Math.abs(mcaps[hi] - value)) ? lo : hi
}

// --- Bucket size picker ---
// ── Log lattice: the ratio ladder ───────────────────────────────────
// MCAP_BUCKET_STEPS is a ladder of absolute row heights, in price. A log
// lattice needs the same thing in RATIO: rungs the sizer can land on so a row
// is a fixed percentage rather than a fixed amount. 1.00001 (0.001% rows)
// matches futures native / pickStorageBucket so a y-zoom-in can actually
// rebucket instead of fattening 0.05% cells; 1.10 covers a memecoin spanning
// two decades.
//
// Rungs are ~1.2-1.5x apart in ln-space — coarser than MCAP_BUCKET_STEPS,
// deliberately. A rung crossing costs a full rebucket, and log's whole point is
// that the range stops swinging, so the sizer should rarely need to move at
// all. The fine end is denser on purpose: futures zoom-in had nowhere to go
// once 1.0005 was the floor.
export const MCAP_RATIO_STEPS = [
  1.00001, 1.000015, 1.00002, 1.00003, 1.00004, 1.00005, 1.000075,
  1.0001, 1.00015, 1.0002, 1.0003, 1.0004, 1.0005, 1.001, 1.0015, 1.002,
  1.003, 1.004, 1.005, 1.0075, 1.01, 1.0125, 1.015, 1.02, 1.025, 1.03,
  1.04, 1.05, 1.075, 1.10,
]

export const snapRatio = (r) => {
  const t = Math.log(Math.max(1 + 1e-12, r))
  let best = MCAP_RATIO_STEPS[0]
  let bestD = Math.abs(Math.log(best) - t)
  for (let i = 1; i < MCAP_RATIO_STEPS.length; i++) {
    const d = Math.abs(Math.log(MCAP_RATIO_STEPS[i]) - t)
    if (d < bestD) { best = MCAP_RATIO_STEPS[i]; bestD = d }
  }
  return best
}

// GLOBAL LATTICE, ANCHORED AT 1.0 — the log counterpart of
// floor(m / bucket) * bucket. Anchor-free is the property that lets the grid
// memo and buildDisplay derive the same row edge from a price without sharing
// state; anchoring at rangeMax would look correct and drift the moment the
// range moved.
//
// ln(ratio) IS MEMOISED, AND IT MATTERS MORE THAN IT LOOKS.
// Both of these took Math.log(ratio) on EVERY call, and ratio is the
// lattice's geometric step — constant for a whole frame, changing only on a
// rebucket. So the divisor was being recomputed once per cell per frame.
//
// A profile put these two at 128.8ms and 39.1ms of SELF time — 168ms
// combined, ~8% of the trace and the largest JS cost after the frame
// driver's own dispatch. logStepEdge's Math.log(ratio) WAS its entire body
// apart from an exp; logStepIdx still needs its per-price Math.log(m), but
// loses the second log and the divide setup.
//
// One entry is the right size: every caller inside a frame passes the same
// ratio, and a rebucket simply replaces it. Keyed on the value, not a
// generation, so a caller that legitimately alternates ratios still gets
// correct answers — just without the win.
//
// LOG MODE ONLY. These are the log counterpart of floor(m / bucket), so a
// linear chart never enters here. That is why the cost came and went.
let _lnRatio = 0
let _lnRatioFor = 0
const lnRatioOf = (ratio) => {
  if (ratio !== _lnRatioFor) { _lnRatioFor = ratio; _lnRatio = Math.log(ratio) }
  return _lnRatio
}
export const logStepIdx  = (m, ratio) => Math.floor(Math.log(m) / lnRatioOf(ratio) + 1e-9)
export const logStepEdge = (k, ratio) => Math.exp(k * lnRatioOf(ratio))

export function pickBucketForRange(mcapRange, containerHeight) {
  if (mcapRange <= 0 || containerHeight <= 0) return 100
  const targetRows = Math.max(1, Math.floor(containerHeight / ROW_HEIGHT))
  const idealBucket = mcapRange / targetRows
  for (const step of MCAP_BUCKET_STEPS) {
    if (step >= idealBucket) return step
  }
  return MCAP_BUCKET_STEPS[MCAP_BUCKET_STEPS.length - 1]
}

// --- Storage bucket picker ---
// Controls how much market-cap detail is kept for live trades and persisted to IDB.
// Two constraints:
//   1. Resolution — finer percent means more detail. 0.00001 (0.001%) gives
//      $0.025 native at $2260 price, $10 at $1M mcap. Modest memory cost
//      (~12KB per candle worst case) for major fidelity improvement at
//      both ends of the price range. Old default 0.0005 was tuned for
//      Solana memecoin mcaps and broke in futures: at $2260 it picked
//      $1.25 native, coarser than typical $0.15 display → display
//      cells clamped to native grid → visible misalignment.
//   2. Display fit — native MUST be ≤ displayBucket so every display
//      cell aligns to native granularity. If percent picks something
//      coarser, clamp to the largest step ≤ displayBucket.
export function pickStorageBucket(referenceMcap, displayBucket = null, percent = 0.00001) {
  if (!referenceMcap || referenceMcap <= 0) return MCAP_BUCKET_STEPS[0]
  const target = referenceMcap * percent
  let pick = MCAP_BUCKET_STEPS[MCAP_BUCKET_STEPS.length - 1]
  for (const step of MCAP_BUCKET_STEPS) {
    if (step >= target) { pick = step; break }
  }
  if (displayBucket && displayBucket > 0 && pick > displayBucket) {
    // Find largest step ≤ displayBucket and clamp.
    let cap = MCAP_BUCKET_STEPS[0]
    for (const step of MCAP_BUCKET_STEPS) {
      if (step > displayBucket) break
      cap = step
    }
    pick = cap
  }
  return pick
}

// ============================================
// --- Mcap ↔ Y coordinate helpers ---
// Single source of truth for translating between market-cap values and
// pixel Y positions inside the chart body. Row-EDGE convention (the
// footprint's own bucketing — keys are floors):
//   - Row i holds [sortedMcaps[i], sortedMcaps[i] + step) and spans
//     y = i*RH (top) .. (i+1)*RH (bottom).
//   - So a key sortedMcaps[i] maps to its row's BOTTOM edge: y = (i + 1) * RH,
//     and a price inside the bucket lands inside the row/cell.
//   - (Was row-centre, y = (i + 0.5) * RH: everything continuous plotted
//     half a row above the cells.)
// Both the canvas renderer and the interaction hook MUST use these so
// cursor labels, hover fills, AVWAP / S/R lines, OHLC wicks, the current-
// price highlight, and footprint cells all agree on where a given mcap
// sits on screen.
// ============================================
export function mcapToY(mcap, sortedMcaps, rowHeight) {
  if (!sortedMcaps || sortedMcaps.length < 2 || rowHeight <= 0) return 0
  // THE continuous price→pixel. Wicks, bodies, the trail, AVWAP, S/R, swings,
  // flip zones, alert and position tags all come through here, so this is
  // where a log lattice either holds together or falls apart.
  //
  // Same expression as latticeRowIndex under a monotone transform:
  //   linear  rowF = (top − m) / (top − next)
  //   log     rowF = ln(top / m) / ln(top / next)
  // Left linear on a log lattice, everything continuous is placed by linear
  // interpolation across a geometric grid — exact at the top row and further
  // off the lower you go, while the CELLS (which route through buildDisplay
  // and findBucketIndex) sit correctly. Wicks disagreeing with their own
  // candle's cells about price is the one thing this chart must never show.
  //
  // Reads the mode off the array like the discrete mappers do, so an
  // untagged (linear) lattice is byte-identical to before.
  if (sortedMcaps.log) {
    if (!(mcap > 0)) return 0
    const step = Math.log(sortedMcaps[0] / sortedMcaps[1])
    if (!(step > 0) || !Number.isFinite(step)) return 0
    const rowF = Math.log(sortedMcaps[0] / mcap) / step
    return (rowF + 1) * rowHeight
  }
  const bucketSize = sortedMcaps[0] - sortedMcaps[1]
  if (!bucketSize || !Number.isFinite(bucketSize)) return 0
  const rowF = (sortedMcaps[0] - mcap) / bucketSize
  return (rowF + 1) * rowHeight
}

export function yToMcap(y, sortedMcaps, rowHeight) {
  if (!sortedMcaps || sortedMcaps.length < 2 || rowHeight <= 0) return 0
  const rowF = y / rowHeight - 1   // exact inverse of mcapToY (row-edge)
  // LOG: the inverse of the lattice, in its own space. A pixel is a constant
  // fraction of a row either way; a row is a constant difference (linear) or a
  // constant ratio (log). Interpolating linearly on a log lattice would put
  // the crosshair between rows by up to half a row's ratio at the top of a
  // wide range — the price readout would disagree with the axis beside it.
  if (sortedMcaps.log) {
    const step = Math.log(sortedMcaps[0] / sortedMcaps[1])
    if (!(step > 0) || !Number.isFinite(step)) return 0
    return Math.exp(Math.log(sortedMcaps[0]) - rowF * step)
  }
  const bucketSize = sortedMcaps[0] - sortedMcaps[1]
  if (!bucketSize || !Number.isFinite(bucketSize)) return 0
  return sortedMcaps[0] - rowF * bucketSize
}

// --- Candle deduplication ---
// When two candles share a startTime, merge their level data when both
// are live (_liveLevels Map) — the original use case is rare WS-batch
// collisions on the same time slot. When either side is packed (_native
// Float64Array) or otherwise non-mergeable, keep whichever carries more
// total volume. The live-on-live merge path is preserved.
export function deduplicateCandles(candles) {
  const seen = new Map()
  for (const candle of candles) {
    const existing = seen.get(candle.startTime)
    if (existing) {
      const eLive = !existing._native && existing._liveLevels
      const cLive = !candle._native   && candle._liveLevels
      if (eLive && cLive) {
        // Both live — accumulate by stepIdx. If they share a _nativeBucket
        // we can merge directly; otherwise fall back to volume comparison.
        if (existing._nativeBucket === candle._nativeBucket) {
          for (const [step, level] of candle._liveLevels) {
            let lvl = existing._liveLevels.get(step)
            if (!lvl) { lvl = { buy: 0, sell: 0 }; existing._liveLevels.set(step, lvl) }
            lvl.buy  += level.buy  || 0
            lvl.sell += level.sell || 0
          }
          existing._display       = null
          existing._displayBucket = null
        } else if (candleTotalVolume(candle) > candleTotalVolume(existing)) {
          seen.set(candle.startTime, candle)
        }
      } else {
        // At least one side is packed / nulled — pick richer by total volume.
        if (candleTotalVolume(candle) > candleTotalVolume(existing)) {
          seen.set(candle.startTime, candle)
        }
      }
    } else {
      seen.set(candle.startTime, candle)
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.startTime - b.startTime)
}

// --- Text sprite cache for canvas performance ---
// Backed by a Map for native insertion-order semantics, used as an LRU:
//   - On hit: delete + re-set so the entry moves to the end (most-recent)
//   - On miss + at-cap: delete the FIRST entry (least-recent) and add new
// This replaces the previous "nuke at 2000" behavior, which caused a
// noticeable frame hitch every time the cache filled — every visible
// cell needed its sprite re-rendered in the next paint. With LRU,
// eviction is steady-state and one entry at a time.
const textSpriteCache = new Map()
const MAX_SPRITE_CACHE_SIZE = 8000

export function getTextSprite(font, color, text) {
  const key = `${font}|${color}|${text}`
  const cached = textSpriteCache.get(key)
  if (cached) {
    // Bump to most-recent
    textSpriteCache.delete(key)
    textSpriteCache.set(key, cached)
    return cached
  }
  if (textSpriteCache.size >= MAX_SPRITE_CACHE_SIZE) {
    // Evict the oldest single entry (Map iteration order = insertion order)
    const oldestKey = textSpriteCache.keys().next().value
    if (oldestKey !== undefined) textSpriteCache.delete(oldestKey)
  }

  const dpr = window.devicePixelRatio || 1
  const off = document.createElement('canvas')
  const offCtx = off.getContext('2d')

  offCtx.font = font
  const metrics = offCtx.measureText(text)
  const padding = 4
  const cssW = Math.ceil(metrics.width) + padding * 2
  const cssH = 16

  off.width = cssW * dpr
  off.height = cssH * dpr
  off._cssW = cssW
  off._cssH = cssH

  offCtx.scale(dpr, dpr)
  offCtx.font = font
  offCtx.textBaseline = 'middle'
  offCtx.fillStyle = color
  // Optical adjustment: 'middle' baseline reserves descender space below
  // the digit cap, so digits visually float ~1px high relative to the
  // sprite's geometric center. Nudging Y down by 1px brings them onto
  // the perceived center.
  offCtx.fillText(text, padding, cssH / 2 + 1)

  textSpriteCache.set(key, off)
  return off
}

export function clearTextSpriteCache() {
  textSpriteCache.clear()
}

// --- Atlas-backed text sprites ---
// Same rasterization as getTextSprite, different residence: every sprite
// lives inside a small number of SHARED atlas canvases instead of its own.
// Why it matters: drawImage cost is dominated by SOURCE BINDING — each
// distinct source canvas breaks the browser's draw batching, so 3,000 cell
// labels from 3,000 tiny canvases are 3,000 unbatchable blits (the profile's
// 610ms). From ONE atlas they are same-source and batch aggressively.
//
// Shelf packer: rows of uniform height (sprites share cssH), left-to-right,
// new shelf when a row fills, new page when a page fills.
//
// ONE RASTER SIZE. Zoom used to bake font px into the key/partition, so
// crossing 12→14 cold-started every glyph — scar tissue (steps, hysteresis,
// per-font LRU) managed a cliff that only existed because size was identity.
// Rasterise at CELL_ATLAS_PX (top of the 8..16 range) and scale at draw;
// the key is colour|text. drawSpriteBatch already takes dest quads.
export const CELL_ATLAS_PX = 16
export const CELL_ATLAS_FONT = `200 ${CELL_ATLAS_PX}px "JetBrains Mono", monospace`
// Weight 500, same 16px raster. Footprint digits use this only at the 10px
// floor. 11-16px cells stay on CELL_ATLAS_FONT (weight 200). Not an outline.
export const CELL_ATLAS_FONT_HEAVY = `400 ${CELL_ATLAS_PX}px "JetBrains Mono", monospace`
// cell-text-scale-20261019: second raster for tall rows (17..24px), so big
// cell text is drawn from a 24px raster instead of upscaling the 16px one.
export const CELL_ATLAS_PX_LARGE = 24
export const CELL_ATLAS_FONT_LARGE = `200 ${CELL_ATLAS_PX_LARGE}px "JetBrains Mono", monospace`
const _atlasFontOf = (font) => (font === CELL_ATLAS_FONT_HEAVY || font === CELL_ATLAS_FONT_LARGE) ? font : CELL_ATLAS_FONT
const _atlasPxOf = (font) => font === CELL_ATLAS_FONT_LARGE ? CELL_ATLAS_PX_LARGE : CELL_ATLAS_PX

const ATLAS_PAGE = 1024
const ATLAS_MAX_PAGES = 4

// Kept as a Map for the old alloc/evict shape. Tenants are CELL_ATLAS_FONT
// (weight 200) and, for the 10px floor only, CELL_ATLAS_FONT_HEAVY (weight 500).
const atlasFonts = new Map()   // font -> { pages, cache, lru }
let atlasLruTick = 0
let atlasTotalPages = 0
// Bumped by every reset OR eviction. Buckets (below) hold entries pointing
// INTO pages; discarding pages must invalidate them. A bucket is held by the
// CALLER across the call that frees them, so it cannot be reached from here
// to clear — it self-heals on its next read by comparing this counter.
let atlasGen = 0
// PER-FONT generation, alongside the global one. Eviction frees ONE font's
// pages, so it must invalidate ONE font's buckets — bumping atlasGen cleared
// every bucket of every font, including the one being drawn with. Its pages
// survived, but its bucket entries did not, so every number on the chart
// re-rasterised anyway and could trigger the next eviction. That is the
// "cross a size, overflow, re-rasterise everything, repeat" cliff the
// quantiseCellFont note describes: per-font eviction fixed WHICH pages are
// freed and left the invalidation wholesale.
//
// Survives the font's deletion and only ever increments, so a font that is
// evicted and later rebuilt gets a higher number and stale buckets still
// invalidate. Bounded by the count of distinct font strings.
const atlasFontGen = new Map()
function _fontGen(font) { return atlasFontGen.get(font) || 0 }

function _fontSlot(font) {
  let f = atlasFonts.get(font)
  if (!f) { f = { pages: [], cache: new Map(), lru: 0 }; atlasFonts.set(font, f) }
  f.lru = ++atlasLruTick
  return f
}

function _atlasReset() {
  // Wholesale: every page is gone, so the GLOBAL generation is the right
  // hammer. Per-font counters reset with it — nothing they could still
  // point into remains.
  atlasFonts.clear()
  atlasFontGen.clear()
  atlasTotalPages = 0
  atlasGen++
}

// Free the least-recently-used font that is NOT the one being drawn with.
// Returns false when there is nothing else to give up.
function _evictLruFont(keepFont) {
  let victim = null, oldest = Infinity
  for (const [font, f] of atlasFonts) {
    if (font === keepFont) continue
    if (f.lru < oldest) { oldest = f.lru; victim = font }
  }
  if (victim === null) return false
  atlasTotalPages -= atlasFonts.get(victim).pages.length
  atlasFonts.delete(victim)
  // ONLY the victim. Bumping atlasGen here was the cliff — see _fontGen.
  atlasFontGen.set(victim, _fontGen(victim) + 1)
  return true
}

function _atlasAlloc(font, sw, sh) {
  const f = _fontSlot(font)
  let pg = f.pages[f.pages.length - 1]
  if (!pg || pg.x + sw > ATLAS_PAGE && pg.y + pg.shelfH + sh > ATLAS_PAGE) {
    if (!pg || atlasTotalPages < ATLAS_MAX_PAGES) {
      const canvas = document.createElement('canvas')
      canvas.width = ATLAS_PAGE; canvas.height = ATLAS_PAGE
      pg = { canvas, ctx: canvas.getContext('2d'), x: 0, y: 0, shelfH: sh, epoch: 0 }
      canvas._atlasEpoch = 0
      f.pages.push(pg)
      atlasTotalPages++
    } else if (_evictLruFont(font)) {
      // Space reclaimed from a font we are no longer drawing with. Retry —
      // the branch above now takes the "create a page" path.
      return _atlasAlloc(font, sw, sh)
    } else {
      // This font is the only tenant and has filled the whole atlas on its
      // own. Nothing to trade against, so the old behaviour is correct here:
      // drop everything and start over.
      _atlasReset()
      return _atlasAlloc(font, sw, sh)
    }
  }
  if (pg.x + sw > ATLAS_PAGE) {           // shelf full -> next shelf
    pg.x = 0
    pg.y += pg.shelfH
    pg.shelfH = sh
  }
  if (pg.y + sh > ATLAS_PAGE) {           // page full -> recurse into new page
    pg.x = ATLAS_PAGE; pg.y = ATLAS_PAGE  // poison so the top branch rolls over
    return _atlasAlloc(font, sw, sh)
  }
  if (sh > pg.shelfH) pg.shelfH = sh
  const out = { pg, sx: pg.x, sy: pg.y }
  pg.x += sw
  return out
}

export function getTextSpriteAtlas(font, color, text) {
  // Size is always CELL_ATLAS_PX. Weight stays 200 unless the caller passes
  // CELL_ATLAS_FONT_HEAVY. Any other font string (including old size strings)
  // is the shared 200 atlas; draw sites scale with (desiredPx / CELL_ATLAS_PX).
  const atlasFont = _atlasFontOf(font)
  const f = _fontSlot(atlasFont)
  const key = `${color}|${text}`
  const hit = f.cache.get(key)
  if (hit) return hit

  const dpr = window.devicePixelRatio || 1
  if (f.pages.length === 0) _atlasAlloc(atlasFont, 1, 1)
  const mctx = f.pages[0].ctx
  mctx.font = atlasFont
  const padding = 4
  const cssW = Math.ceil(mctx.measureText(text).width) + padding * 2
  const cssH = _atlasPxOf(atlasFont)
  const sw = Math.ceil(cssW * dpr)
  const sh = Math.ceil(cssH * dpr)

  const { pg, sx, sy } = _atlasAlloc(atlasFont, sw, sh)
  const c = pg.ctx
  c.save()
  c.setTransform(dpr, 0, 0, dpr, sx, sy)
  c.font = atlasFont
  c.textBaseline = 'middle'
  c.fillStyle = color
  // Same 1px optical nudge as getTextSprite — see its comment.
  c.fillText(text, padding, cssH / 2 + 1)
  c.restore()
  pg.epoch = (pg.epoch || 0) + 1
  pg.canvas._atlasEpoch = pg.epoch

  const entry = { img: pg.canvas, sx, sy, sw, sh, _cssW: cssW, _cssH: cssH, _atlasPx: cssH }
  _fontSlot(atlasFont).cache.set(key, entry)
  return entry
}

// Draw width of a label in atlas CSS px, same formula as getTextSpriteAtlas
// (_cssW = ceil(measureText) + 4px pad each side). Callers scale by
// fontSize/CELL_ATLAS_PX. Does not allocate a sprite — fit checks must
// run before atlasText so a shortened label is the string that is rasterised.
const ATLAS_PAD = 4
const atlasWidthCache = new Map() // font -> Map(text -> cssW)
let atlasMeasureCtx = null
let atlasMeasureFont = ''
export function atlasCssWidth(font, text) {
  const atlasFont = _atlasFontOf(font)
  const s = String(text)
  let byText = atlasWidthCache.get(atlasFont)
  if (!byText) { byText = new Map(); atlasWidthCache.set(atlasFont, byText) }
  const hit = byText.get(s)
  if (hit !== undefined) return hit
  if (!atlasMeasureCtx) atlasMeasureCtx = document.createElement('canvas').getContext('2d')
  if (atlasMeasureFont !== atlasFont) {
    atlasMeasureCtx.font = atlasFont
    atlasMeasureFont = atlasFont
  }
  const cssW = Math.ceil(atlasMeasureCtx.measureText(s).width) + ATLAS_PAD * 2
  byText.set(s, cssW)
  return cssW
}

// ── Bucketed atlas access, for hot loops ─────────────────────────────────
// getTextSpriteAtlas builds `${font}|${color}|${text}` on EVERY call — a
// ~45-char string allocated and hashed per lookup. The footprint cell loop
// calls it twice per cell, so at full density (~8.7k cells, the LOD ceiling)
// that is ~17k allocations and 17k long-string hashes per frame: measured at
// 4.7ms, roughly 28% of a 16.7ms frame, to look up text that has not changed.
//
// But `font` and `color` are near-constant within a frame — one font, and a
// handful of colours off the theme LUT. So resolve them ONCE outside the
// loop and index the remainder by the short text alone. Measured 5x on the
// resolution path.
//
// Deliberately NOT a per-candle sprite-ref cache. That measures ~157x, but
// it needs a parallel structure invalidated on bucket, data version AND
// theme; this needs none of that and captures most of the win. The last
// 0.9ms/frame is not worth a third invalidation rule in the cell path.
//
// USAGE — bucket hoisted out of the loop, one lookup inside it:
//   const buys = atlasBucket(font, buyTextColor)
//   for (…cells…) drawImage(atlasText(buys, formatCellVol(v)), …)
//
// A bucket is a plain holder, cheap to make and safe to keep for a frame.
// Do not cache one across frames: `color` is theme-derived, and a theme
// change must produce a new bucket rather than reuse a stale one.
const atlasTiers = new Map()   // font -> color -> bucket

export function atlasBucket(fontOrColor, maybeColor) {
  // atlasBucket(color) or atlasBucket(font, color).
  // font is ignored unless it is CELL_ATLAS_FONT_HEAVY (weight 500, same 16px).
  const color = maybeColor !== undefined ? maybeColor : fontOrColor
  const font = maybeColor !== undefined ? _atlasFontOf(fontOrColor) : CELL_ATLAS_FONT
  let byColor = atlasTiers.get(font)
  if (!byColor) { byColor = new Map(); atlasTiers.set(font, byColor) }
  let b = byColor.get(color)
  if (!b) { b = { font, color, map: new Map(), gen: atlasGen, fgen: _fontGen(font) }; byColor.set(color, b) }
  return b
}

export function atlasText(bucket, text) {
  // Generation check, not a cleared-from-outside cache: _atlasReset can fire
  // MID-LOOP (a page overflow during this very frame's first miss), and the
  // caller is holding this bucket. Without the check, every entry cached
  // before that reset would keep being served, pointing into pages that no
  // longer exist — blank or garbled numbers, intermittently, only on charts
  // dense enough to overflow four 1024² pages. One integer compare per
  // lookup buys that away.
  // Two counters: the global one for a wholesale reset, the per-font one for
  // an eviction of THIS font. A different font being evicted no longer
  // touches this bucket.
  let _fg = _fontGen(bucket.font)
  if (bucket.gen !== atlasGen || bucket.fgen !== _fg) {
    bucket.map.clear(); bucket.gen = atlasGen; bucket.fgen = _fg
  }
  const hit = bucket.map.get(text)
  if (hit) return hit
  // Miss → the full path, which also populates the font partition so the two stay
  // consistent for any caller still using it directly. Re-read the
  // generation afterwards: the call may itself have triggered the reset.
  const e = getTextSpriteAtlas(bucket.font, bucket.color, text)
  _fg = _fontGen(bucket.font)
  if (bucket.gen !== atlasGen || bucket.fgen !== _fg) {
    bucket.map.clear(); bucket.gen = atlasGen; bucket.fgen = _fg
  }
  bucket.map.set(text, e)
  return e
}

// --- CME futures (tick-stream.xyz via our server; realtime plan, live) ---
// Picked from the FUTURES dropdown's CME tab and ridden through the same
// futures path as HL coins (hl_subscribe / /fut/profile); the server routes
// these roots to its tickstream feed. Tick 0.25 for all three; volume = contracts.
export const CME_FUTURES = {   // tslive-20261103: Realtime plan, 25 symbols (server tickstream_feed.SYMBOLS)
  ES:  { name: 'E-mini S&P 500', tick: 0.25 },
  MES: { name: 'Micro E-mini S&P 500', tick: 0.25 },
  NQ:  { name: 'E-mini Nasdaq-100', tick: 0.25 },
  MNQ: { name: 'Micro E-mini Nasdaq-100', tick: 0.25 },
  YM:  { name: 'E-mini Dow', tick: 1 },
  MYM: { name: 'Micro E-mini Dow', tick: 1 },
  RTY: { name: 'E-mini Russell 2000', tick: 0.1 },
  M2K: { name: 'Micro E-mini Russell 2000', tick: 0.1 },
  ZN:  { name: '10-Year T-Note', tick: 0.015625 },
  ZB:  { name: '30-Year T-Bond', tick: 0.03125 },
  ZF:  { name: '5-Year T-Note', tick: 0.0078125 },
  ZT:  { name: '2-Year T-Note', tick: 0.00390625 },
  CL:  { name: 'Crude Oil', tick: 0.01 },
  MCL: { name: 'Micro Crude Oil', tick: 0.01 },
  RB:  { name: 'RBOB Gasoline', tick: 0.0001 },
  HO:  { name: 'Heating Oil', tick: 0.0001 },
  GC:  { name: 'Gold', tick: 0.10 },
  MGC: { name: 'Micro Gold', tick: 0.10 },
  SI:  { name: 'Silver', tick: 0.005 },
  HG:  { name: 'Copper', tick: 0.0005 },
  PL:  { name: 'Platinum', tick: 0.1 },
  '6E': { name: 'Euro FX', tick: 0.00005 },
  '6B': { name: 'British Pound', tick: 0.0001 },
  '6J': { name: 'Japanese Yen', tick: 0.0000005 },
  '6C': { name: 'Canadian Dollar', tick: 0.00005 },
}
export const CME_FEED_DELAY_MS = 0   // tslive-20261103: realtime plan, no delay
export const cmeInfo = (coin) => CME_FUTURES[String(coin || '').toUpperCase()] || null

// --- Stocks + options (Schwab via Corsair relay -> our server; schwab-20261103) ---
// STK:<ticker> / STK-OPT:<schwab option symbol>. Data only, never traded.
// Footprint is ESTIMATED: Schwab has no equity tape, prints are derived from
// ~1s level-one volume deltas (quote rule, then tick rule).
export const STOCKS = {
  SPY: 'SPDR S&P 500', QQQ: 'Invesco QQQ', IWM: 'iShares Russell 2000', DIA: 'SPDR Dow',
  AAPL: 'Apple', MSFT: 'Microsoft', NVDA: 'NVIDIA', TSLA: 'Tesla', AMZN: 'Amazon',
  META: 'Meta', GOOGL: 'Alphabet', AMD: 'AMD', PLTR: 'Palantir', COIN: 'Coinbase', MSTR: 'Strategy', SPCX: 'SpaceX',
}
export const stkInfo = (coin) => {
  const v = String(coin || '')
  const m = /^(STK|STK-OPT):(.+)$/i.exec(v)
  if (!m) return null
  const opt = m[1].toUpperCase() === 'STK-OPT'
  return { opt, sym: m[2], tick: 0.01, name: opt ? 'Option ' + m[2].replace(/\s+/g, ' ') : (STOCKS[m[2].toUpperCase()] || m[2]) }
}
export const stkCoin = (s) => { const i = stkInfo(s); return i ? (i.opt ? 'STK-OPT:' : 'STK:') + i.sym.toUpperCase() : null }

// --- Historical candle fetcher (Hyperliquid futures) ---
export async function fetchHyperliquidCandles(coin, timeIntervalSeconds) {
  try {
    if (stkInfo(coin)) {
      const candleMs = Math.max(15, timeIntervalSeconds || 60) * 1000
      const res = await fetch(`${HTTP_PROXY_BASE}/stk/klines?coin=${encodeURIComponent(stkCoin(coin))}&interval=${candleMs / 1000}&limit=2880`)
      const rows = await res.json()
      if (!Array.isArray(rows)) return []
      return rows.map(c => ({
        startTime: Math.floor(c.t / candleMs) * candleMs, isHistorical: true,
        open_mcap: parseFloat(c.o), high_mcap: parseFloat(c.h), low_mcap: parseFloat(c.l),
        close_mcap: parseFloat(c.c), volume_tokens: parseFloat(c.v),
      })).sort((a, b) => a.startTime - b.startTime)
    }
    if (cmeInfo(coin)) {
      // CME: server builds candles from ticks at the chart's own interval.
      const candleMs = Math.max(15, timeIntervalSeconds || 60) * 1000
      const res = await fetch(`${HTTP_PROXY_BASE}/cme/klines?coin=${encodeURIComponent(coin)}&interval=${candleMs / 1000}&limit=2880`)
      const rows = await res.json()
      if (!Array.isArray(rows)) return []
      return rows.map(c => ({
        startTime: Math.floor(c.t / candleMs) * candleMs,
        isHistorical: true,
        open_mcap: parseFloat(c.o),
        high_mcap: parseFloat(c.h),
        low_mcap: parseFloat(c.l),
        close_mcap: parseFloat(c.c),
        volume_tokens: parseFloat(c.v),
      })).sort((a, b) => a.startTime - b.startTime)
    }
    // Map interval to HL format. THE RULE: a kline interval may be COARSER
    // than the chart timeframe, never finer. Coarser is safe because a
    // coarse timestamp is always a valid slot boundary — sub-minute charts
    // take 1m klines (HL's finest) and render that sparser lattice as-is.
    // FINER is the failure mode: the store fills with candles at timestamps
    // between the chart's slots, dedupe keys on startTime so nothing
    // collapses them, and they render as extra candles beside the real
    // ones.
    //
    // 1800 was that failure for a long time: '15m' filled the 30m chart
    // with :15/:45 klines. The old comment said to keep this map "in
    // lockstep with OM_INTERVAL_MAP — same lattice, or the profile
    // map-merge misses every kline", and both maps dutifully said 15m, so
    // the two feeds agreed with each other and BOTH sat on the wrong grid.
    // That contract is superseded: klines now fetch at the CHART's own
    // interval (HL serves 30m natively), and the profile side — whose API
    // has no thirty-minute period — keeps 15m and is folded onto the chart
    // grid by foldProfilesToSlot before conversion. Each feed must land on
    // the chart lattice; they no longer need to match each other.
    //
    // Every OFFERED timeframe still needs its own row: a missing key
    // silently falls back to '1m', which is how the 15m chart once spent a
    // while rendering 500 one-minute candles as its "history" (the
    // stacked-columns screenshot).
    const intervalMap = {
      15: '1m', 30: '1m', 60: '1m', 300: '5m',
      900: '15m',
      1800: '30m', 3600: '1h', 14400: '4h', 86400: '1d',
    }
    const hlInterval = intervalMap[timeIntervalSeconds] || '1m'

    // Fetch last ~500 candles
    const now = Date.now()
    // Estimate how far back we need
    // MUST carry a row for every value intervalMap can produce. The `|| 60000`
    // fallback is silent and wrong in the same direction every time: it asks
    // for 500 MINUTES of history (8.3h) while requesting a coarser interval,
    // so the response is correct but far too short. Cell candles older than
    // that window then find no kline in ohlcByTs and fall back to `mean` for
    // open AND close — a chart full of flat candles that opened where they
    // closed. That is exactly what adding '30m' to intervalMap without a row
    // here produced.
    const hlIntervalMs = {
      '1m': 60000, '5m': 300000, '15m': 900000, '30m': 1800000,
      '1h': 3600000, '4h': 14400000, '1d': 86400000,
    }
    const candleMs = hlIntervalMs[hlInterval] || 60000
    const startTime = now - (500 * candleMs)

    const res = await fetch('https://api.hyperliquid.xyz/info', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'candleSnapshot',
        req: { coin, interval: hlInterval, startTime },
      }),
    })
    const candles = await res.json()
    if (!Array.isArray(candles)) return []

    // Align to the HL native interval (not the display interval)
    // so candles aren't sparse when display interval < HL interval
    return candles.map(c => {
      const ts = c.t  // open time in ms
      const open = parseFloat(c.o)
      const high = parseFloat(c.h)
      const low = parseFloat(c.l)
      const close = parseFloat(c.c)
      const volume = parseFloat(c.v)
      return {
        startTime: Math.floor(ts / candleMs) * candleMs,
        isHistorical: true,

        open_mcap: open,
        high_mcap: high,
        low_mcap: low,
        close_mcap: close,
        volume_tokens: volume,
      }
    }).sort((a, b) => a.startTime - b.startTime)
  } catch (err) {
    console.warn('[Hyperliquid History] Fetch failed:', err)
    return []
  }
}

// --- Historical candle fetcher (Solana) ---
export async function fetchHistoricalCandles(mintAddress, timeIntervalSeconds, tokenSupply) {
  try {
    // 1. Resolve pool address via DexScreener
    const dexRes = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${mintAddress}`)
    const dexData = await dexRes.json()
    const pairs = (dexData.pairs || []).filter(p => p.chainId === 'solana')
    if (!pairs.length) return []
    const pair = pairs.sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0]
    const poolAddress = pair.pairAddress

    // 2. Current SOL/USD price for conversion
    let solPrice = 100
    try {
      const solRes = await fetch('https://api.kraken.com/0/public/Ticker?pair=SOLUSD')
      const solData = await solRes.json()
      solPrice = parseFloat(solData.result?.SOLUSD?.c?.[0]) || 150
    } catch {}

    // 3. Fetch OHLCV from GeckoTerminal
    let endpoint = 'minute'
    let aggregate = 1
    if (timeIntervalSeconds >= 3600) {
      endpoint = 'hour'
      aggregate = 1
    } else if (timeIntervalSeconds >= 900) {
      endpoint = 'minute'
      aggregate = 15
    } else if (timeIntervalSeconds >= 300) {
      endpoint = 'minute'
      aggregate = 5
    } else {
      endpoint = 'minute'
      aggregate = 1
    }

    const gtRes = await fetch(
      `${HTTP_PROXY_BASE}/gt/api/v2/networks/solana/pools/${poolAddress}/ohlcv/${endpoint}?limit=500&aggregate=${aggregate}`,
      { headers: { Accept: 'application/json;version=20230302' } }
    )
    const gtData = await gtRes.json()
    const ohlcvList = gtData.data?.attributes?.ohlcv_list || []

    // 4. Convert USD price → SOL market cap
    const intervalMs = timeIntervalSeconds * 1000
    return ohlcvList
      .map(([ts, open, high, low, close, volume]) => ({
        startTime: Math.floor((ts * 1000) / intervalMs) * intervalMs,
        isHistorical: true,

        open_mcap:    (open  / solPrice) * tokenSupply,
        high_mcap:    (high  / solPrice) * tokenSupply,
        low_mcap:     (low   / solPrice) * tokenSupply,
        close_mcap:   (close / solPrice) * tokenSupply,
        volume_tokens: (close > 0 && volume > 0) ? volume / close : 0,
      }))
      .sort((a, b) => a.startTime - b.startTime)
  } catch (err) {
    console.warn('[History] Fetch failed:', err)
    return []
  }
}
